/**
 * UnitPlans — dedicated dimensioned floor plans for the 3 residential unit types.
 * Renders each unit's overall length × 8' depth outline + room partitions with
 * dimension callouts (feet, inches, and SF).
 *
 * All dimensions are pulled from the backend UnitType.rooms data so any change
 * to geometry.py automatically flows through here.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { KIND_COLORS_2D } from "./buildingHelpers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SCALE = 20; // 1 ft = 20 px — legible on a laptop viewport
const MARGIN = 60;

const UNIT_META = {
  STUDIO_15: { title: "STUDIO", subtitle: "Split half of a 40' container", color: "#22c55e" },
  ONEBR_25:  { title: "1 BEDROOM", subtitle: "Other half of same split 40' container", color: "#3b82f6" },
  TWOBR_40:  { title: "2 BEDROOM", subtitle: "Full dedicated 40' container", color: "#f59e0b" },
};

const ORDER = ["STUDIO_15", "ONEBR_25", "TWOBR_40"];

function DimensionedPlan({ ut }) {
  const meta = UNIT_META[ut.id];
  const L = ut.length_ft;
  const D = ut.depth_ft;
  const totalSF = Math.round(L * D);

  // SVG viewport: L * scale + margins × 2, D * scale + margins × 2 + dimension gutter
  const W = L * SCALE + MARGIN * 2;
  const H = D * SCALE + MARGIN * 2 + 40;

  // Convert local room coords (x, y in feet) → svg px
  const px = (fx) => MARGIN + fx * SCALE;
  const py = (fy) => MARGIN + fy * SCALE;

  // Ticks: draw a dimension line 22 px above the outline showing overall length
  const dimTop = MARGIN - 22;
  const dimBottom = MARGIN + D * SCALE + 22;
  const dimLeft = MARGIN - 22;
  const dimRight = MARGIN + L * SCALE + 22;

  const containerLenLabel = `${L}'-0"`;
  const containerDepthLabel = `${D}'-0"`;

  return (
    <div className="min-w-0" data-testid={`unit-plan-${ut.id}`}>
      <div className="flex items-baseline gap-3 mb-2">
        <div className="text-xs font-mono tracking-[3px] font-bold" style={{ color: meta.color }}>
          {meta.title}
        </div>
        <div className="text-[10px] font-mono text-[#94a3b8]">
          {L}' × {D}' · {totalSF} SF · {meta.subtitle}
        </div>
      </div>

      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="block bg-[#0b0f14] border border-[#1f2937] rounded">
        {/* ==== overall dimension callouts ==== */}
        {/* Top: overall length arrow */}
        <line x1={px(0)} y1={dimTop} x2={px(L)} y2={dimTop} stroke="#facc15" strokeWidth="0.6" />
        <line x1={px(0)} y1={dimTop - 4} x2={px(0)} y2={dimTop + 4} stroke="#facc15" strokeWidth="0.6" />
        <line x1={px(L)} y1={dimTop - 4} x2={px(L)} y2={dimTop + 4} stroke="#facc15" strokeWidth="0.6" />
        <rect x={px(L / 2) - 26} y={dimTop - 8} width="52" height="14" fill="#0b0f14" />
        <text x={px(L / 2)} y={dimTop + 4} textAnchor="middle" fill="#facc15"
              fontSize="12" fontFamily="monospace" fontWeight="700">
          {containerLenLabel}
        </text>

        {/* Left: overall depth arrow */}
        <line x1={dimLeft} y1={py(0)} x2={dimLeft} y2={py(D)} stroke="#facc15" strokeWidth="0.6" />
        <line x1={dimLeft - 4} y1={py(0)} x2={dimLeft + 4} y2={py(0)} stroke="#facc15" strokeWidth="0.6" />
        <line x1={dimLeft - 4} y1={py(D)} x2={dimLeft + 4} y2={py(D)} stroke="#facc15" strokeWidth="0.6" />
        <text x={dimLeft - 4} y={py(D / 2)} textAnchor="middle" fill="#facc15"
              fontSize="12" fontFamily="monospace" fontWeight="700"
              transform={`rotate(-90 ${dimLeft - 4} ${py(D / 2)})`}>
          {containerDepthLabel}
        </text>

        {/* Container outline — thick */}
        <rect x={px(0)} y={py(0)} width={L * SCALE} height={D * SCALE}
              fill="none" stroke={meta.color} strokeWidth="2" rx="1" />

        {/* Rooms + labels + dimensions */}
        {ut.rooms.map((r, idx) => {
          const rc = KIND_COLORS_2D[r.kind] || "#334155";
          const label = r.name;
          const sf = r.sf ? `${r.sf} SF` : "";
          const rw = r.w;
          const rh = r.h;
          const rx = r.x;
          const ry = r.y;
          const showTick = rw >= 3 && rh >= 3;
          return (
            <g key={idx}>
              <rect x={px(rx)} y={py(ry)} width={rw * SCALE} height={rh * SCALE}
                    fill={rc} fillOpacity="0.4" stroke={rc} strokeWidth="0.9" />
              {/* Room label */}
              <text x={px(rx + rw / 2)} y={py(ry + rh / 2) - 5}
                    textAnchor="middle" fill="#e6edf3" fontSize="9.5"
                    fontFamily="monospace" fontWeight="700" letterSpacing="1">
                {label}
              </text>
              {/* Room dimensions (Wft × Hft) */}
              {showTick && (
                <text x={px(rx + rw / 2)} y={py(ry + rh / 2) + 9}
                      textAnchor="middle" fill="#cbd5e1" fontSize="10"
                      fontFamily="monospace">
                  {`${rw}' × ${rh}'`}
                </text>
              )}
              {sf && showTick && (
                <text x={px(rx + rw / 2)} y={py(ry + rh / 2) + 22}
                      textAnchor="middle" fill="#94a3b8" fontSize="9"
                      fontFamily="monospace" letterSpacing="1">
                  {sf}
                </text>
              )}
              {/* Egress-window star */}
              {r.egress_window && (
                <text x={px(rx + rw - 1.5)} y={py(ry + 1.8)} textAnchor="middle" fill="#fca5a5" fontSize="10">
                  ✱
                </text>
              )}
            </g>
          );
        })}

        {/* Doors */}
        {ut.doors.map((d, di) => {
          let x1, y1, x2, y2;
          const w = d.width;
          if (d.side === "bottom") { x1 = px(d.pos - w / 2); y1 = py(D); x2 = px(d.pos + w / 2); y2 = py(D); }
          else if (d.side === "top") { x1 = px(d.pos - w / 2); y1 = py(0); x2 = px(d.pos + w / 2); y2 = py(0); }
          else if (d.side === "left") { x1 = px(0); y1 = py(d.pos - w / 2); x2 = px(0); y2 = py(d.pos + w / 2); }
          else { x1 = px(L); y1 = py(d.pos - w / 2); x2 = px(L); y2 = py(d.pos + w / 2); }
          return <line key={`door-${di}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#22d3ee" strokeWidth="3.5" strokeLinecap="round" />;
        })}

        {/* Bottom: interior dimension chain (segment widths along length) */}
        {(() => {
          // Sort rooms by x to build the width chain along the container length
          const seg = [...ut.rooms]
            .filter((r) => r.y === 0 || r.y + r.h >= D - 0.01 || r.h >= D - 0.01)
            .sort((a, b) => a.x - b.x);
          // Compute unique x boundaries across rooms
          const xs = Array.from(new Set(ut.rooms.flatMap((r) => [r.x, r.x + r.w]))).sort((a, b) => a - b);
          return xs.slice(0, -1).map((x0, i) => {
            const x1 = xs[i + 1];
            const dx = +(x1 - x0).toFixed(1);
            return (
              <g key={`dx-${i}`}>
                <line x1={px(x0)} y1={dimBottom} x2={px(x1)} y2={dimBottom} stroke="#7dd3fc" strokeWidth="0.6" />
                <line x1={px(x0)} y1={dimBottom - 4} x2={px(x0)} y2={dimBottom + 4} stroke="#7dd3fc" strokeWidth="0.5" />
                <line x1={px(x1)} y1={dimBottom - 4} x2={px(x1)} y2={dimBottom + 4} stroke="#7dd3fc" strokeWidth="0.5" />
                <text x={px((x0 + x1) / 2)} y={dimBottom + 14} textAnchor="middle"
                      fill="#7dd3fc" fontSize="10" fontFamily="monospace" fontWeight="700">
                  {`${dx}'`}
                </text>
              </g>
            );
          });
        })()}
      </svg>
    </div>
  );
}

export default function UnitPlans() {
  const [types, setTypes] = useState([]);

  useEffect(() => {
    axios.get(`${API}/unit-types`)
      .then((res) => setTypes(res.data))
      .catch(() => setTypes([]));
  }, []);

  const rez = types.filter((t) => ORDER.includes(t.id))
                   .sort((a, b) => ORDER.indexOf(a.id) - ORDER.indexOf(b.id));

  return (
    <div className="space-y-6" data-testid="unit-plans-tab">
      <Card className="bg-[#0f172a] border-[#1f2937] p-5">
        <div className="text-[11px] tracking-[0.35em] text-amber-300 font-bold mb-1">
          UNIT PLAN DIMENSIONS
        </div>
        <div className="text-[10px] font-mono text-[#94a3b8]">
          All 3 residential unit types · 40' × 8' × 9'-6" high-cube shipping containers · dimensions in feet
        </div>
      </Card>

      {rez.map((ut) => (
        <Card key={ut.id} className="bg-[#0f172a] border-[#1f2937] p-5 overflow-x-auto">
          <DimensionedPlan ut={ut} />
        </Card>
      ))}

      <Card className="bg-[#0f172a] border-[#1f2937] p-4">
        <div className="text-[10px] tracking-[0.25em] text-[#5b6776] mb-2 font-mono">DIMENSION KEY</div>
        <div className="text-[10px] text-[#cbd5e1] font-mono leading-relaxed space-y-1">
          <div><span className="text-amber-300 font-bold">Yellow</span> — overall container dimensions (length × depth, per ISO 668 40' HC standard)</div>
          <div><span className="text-sky-300 font-bold">Cyan</span> — interior room-width chain along the container length</div>
          <div><span className="text-cyan-300 font-bold">Blue lines on outline</span> — entry doors (3'-0" wide for residential, 4'-0" for commercial)</div>
          <div><span className="text-rose-300 font-bold">✱ Star icon</span> — IRC R310-compliant egress window in the bedroom / sleeping room</div>
          <div className="text-[#94a3b8] mt-2">
            Ceiling height: 9'-6" (2895 mm interior clear, minus 6" for finishes = 9'-0" finished)
          </div>
        </div>
      </Card>
    </div>
  );
}
